import { setFlagCache, type FlagMap } from 'utils/feature-flags/feature-flag-evaluator'

/**
 * Feature flag poller.
 *
 * This is the ONLY feature-flag module that performs I/O (fetch). It loads the
 * flag config from a served `feature-flags.json` into the evaluator's in-memory
 * cache on startup and refreshes it on a configurable interval. The evaluator
 * module (`feature-flag-evaluator.ts`) contains no I/O imports.
 *
 * Poll interval defaults to 30 seconds and is configurable via the
 * `FEATURE_FLAG_POLL_INTERVAL` environment variable (milliseconds).
 *
 * KEY INVARIANT: No runtime network calls in flag evaluation. Only this poller
 * performs network I/O, and it never runs on the synchronous `isOn` path.
 */

const DEFAULT_POLL_INTERVAL_MS = 30_000
const DEFAULT_CONFIG_URL = '/feature-flags.json'

const pollingIntervalMs = (): number => {
  const configured = process.env.FEATURE_FLAG_POLL_INTERVAL
  if (!configured) {
    return DEFAULT_POLL_INTERVAL_MS
  }
  const parsed = Number.parseInt(configured, 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_POLL_INTERVAL_MS
}

let configUrlOverride: string | null = null

export const setConfigUrl = (url: string | null): void => {
  configUrlOverride = url
}

export const getConfigUrl = (): string => configUrlOverride ?? DEFAULT_CONFIG_URL

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

/**
 * Parse a raw flag config payload into a flag map. Ignores entries missing a
 * string `name` or boolean `enabled`.
 */
export const parseFlags = (payload: unknown): FlagMap => {
  const flags: FlagMap = {}
  if (isRecord(payload) && Array.isArray(payload.flags)) {
    for (const entry of payload.flags) {
      if (isRecord(entry) && typeof entry.name === 'string' && typeof entry.enabled === 'boolean') {
        flags[entry.name] = entry.enabled
      }
    }
  }
  return flags
}

/**
 * Fetch the flag config from the configured URL and update the in-memory cache.
 * On any error (network failure, non-OK status, parse error) the cache is left
 * unchanged (graceful degradation: previously-loaded flags remain in effect,
 * and an app that never loaded config stays with all flags false).
 */
export const refreshFlags = async (): Promise<void> => {
  try {
    const response = await fetch(getConfigUrl())
    if (!response.ok) {
      return
    }
    const payload: unknown = await response.json()
    setFlagCache(parseFlags(payload))
  } catch {
    // Network or parse error: leave the cache unchanged.
  }
}

let intervalId: ReturnType<typeof setInterval> | null = null

/**
 * Start the background poller. Idempotent.
 */
export const startPoller = (): void => {
  if (intervalId) {
    return
  }
  intervalId = setInterval(refreshFlags, pollingIntervalMs())
}

/**
 * Stop the background poller and clear the interval.
 */
export const stopPoller = (): void => {
  if (intervalId) {
    clearInterval(intervalId)
    intervalId = null
  }
}
